import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { apiAuth } from "@/lib/api";
import KpiCard from "@/components/admin/KpiCard";
import { StatusBadge } from "@/components/admin/StatusBadge";

const EVENT_LABEL = {
  startup_submitted: "Neue Startup-Einreichung",
  investor_submitted: "Neue Investor-Anmeldung",
  ai_analysis_completed: "AI-Voranalyse fertig",
  ai_analysis_failed: "AI-Voranalyse fehlgeschlagen",
  pitch_deck_uploaded: "Pitch Deck hochgeladen",
  newsletter_subscribed: "Newsletter-Anmeldung",
  contact_submission_received: "Neue Kontaktanfrage",
  startup_status_changed: "Startup-Status geändert",
  investor_status_changed: "Investor-Status geändert",
  admin_login_success: "Admin-Login",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiAuth.get("/admin/dashboard");
        if (!cancelled) setData(data);
      } catch (err) {
        if (!cancelled) setError("Konnte Dashboard nicht laden.");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (error) {
    return <p className="text-coral text-sm" data-testid="dashboard-error">{error}</p>;
  }
  if (!data) {
    return <p className="text-warmGrey text-sm">Lade …</p>;
  }

  const incoming = data.incoming_7d;
  const attention = data.needs_attention;
  const stats = data.stats;

  return (
    <div className="flex flex-col gap-10" data-testid="page-dashboard">
      {/* Section 1: Incoming */}
      <section>
        <h2 className="font-serif text-2xl text-cocoa mb-4">Eingänge dieser Woche</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <KpiCard
            label="Neue Startups" value={incoming.startups} hint="letzte 7 Tage"
            accent="bg-sageMint" testId="kpi-incoming-startups"
            onClick={() => navigate("/admin/startups")}
          />
          <KpiCard
            label="Neue Investor:innen" value={incoming.investors} hint="letzte 7 Tage"
            accent="bg-skyMist" testId="kpi-incoming-investors"
            onClick={() => navigate("/admin/investors")}
          />
          <KpiCard
            label="Newsletter" value={incoming.newsletter} hint="letzte 7 Tage"
            accent="bg-butterCream" testId="kpi-incoming-newsletter"
          />
          <KpiCard
            label="Kontaktanfragen" value={incoming.contact} hint="letzte 7 Tage"
            accent="bg-roseMilk" testId="kpi-incoming-contact"
          />
        </div>
      </section>

      {/* Section 2: Needs attention */}
      <section>
        <h2 className="font-serif text-2xl text-cocoa mb-4">Brauchen deine Aufmerksamkeit</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <KpiCard
            label="Startups zur Review" value={attention.startups_needing_review}
            hint="AI-Voranalyse fertig, Review offen"
            accent="bg-roseMilk" testId="kpi-need-review"
            onClick={() => navigate("/admin/startups?status=submitted,in_review")}
          />
          <KpiCard
            label="Investor:innen offen" value={attention.investors_submitted}
            hint="status = submitted"
            accent="bg-skyMist" testId="kpi-investor-open"
            onClick={() => navigate("/admin/investors?status=submitted")}
          />
          <KpiCard
            label="High Potential" value={attention.high_potential}
            hint="AI-Score ≥ 75"
            accent="bg-sageMint" testId="kpi-high-pot"
            onClick={() => navigate("/admin/startups?min_score=75")}
          />
          <KpiCard
            label="AI-Analysen fehlgeschlagen" value={attention.ai_failed}
            hint="Retry möglich"
            accent="bg-coral/30" testId="kpi-ai-failed"
          />
        </div>
      </section>

      {/* Section 2b: Stale Pipeline */}
      {data.stale_pipeline && (data.stale_pipeline.startups > 0 || data.stale_pipeline.investors > 0) && (
        <section data-testid="section-stale">
          <h2 className="font-serif text-2xl text-cocoa mb-4">
            Stale Pipeline
            <span className="ml-3 text-xs text-warmGrey font-sans align-middle">
              warm leads, die kalt zu werden drohen — kein Update seit {data.stale_pipeline.cutoff_days} Tagen
            </span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <KpiCard
              label="Stale Startups" value={data.stale_pipeline.startups}
              hint="status: submitted/qualified/in_review · updated_at > 14d"
              accent="bg-coral/30" testId="kpi-stale-startups"
              onClick={() => navigate(`/admin/startups?status=submitted,qualified,in_review&submitted_to=${data.stale_pipeline.cutoff_iso.slice(0,10)}`)}
            />
            <KpiCard
              label="Stale Investor:innen" value={data.stale_pipeline.investors}
              hint="status: submitted/qualified · updated_at > 14d"
              accent="bg-coral/30" testId="kpi-stale-investors"
              onClick={() => navigate("/admin/investors?status=submitted,qualified")}
            />
          </div>
        </section>
      )}

      {/* Section 3: Recent activity */}
      <section>
        <h2 className="font-serif text-2xl text-cocoa mb-4">Kürzlich passiert</h2>
        <div className="bg-white border border-sandBeige rounded-3xl divide-y divide-sandBeige/60 overflow-hidden">
          {data.recent_events.length === 0 && (
            <p className="px-6 py-8 text-sm text-warmGrey">Noch keine Aktivitäten.</p>
          )}
          {data.recent_events.map((e) => (
            <div key={e.id} className="px-5 py-3.5 flex items-start gap-4" data-testid={`recent-${e.id}`}>
              <span className="mt-0.5 w-2 h-2 rounded-full bg-raspberry shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-cocoa">
                  {EVENT_LABEL[e.event_type] || e.event_type}
                  {e.metadata?.company ? ` — ${e.metadata.company}` : ""}
                  {e.metadata?.organization ? ` — ${e.metadata.organization}` : ""}
                </p>
                <p className="text-xs text-warmGrey">{new Date(e.timestamp).toLocaleString("de-DE")}</p>
              </div>
              <code className="text-[10px] text-warmGrey/80 hidden md:inline">{e.entity_type}</code>
            </div>
          ))}
        </div>
      </section>

      {/* Section 4: Stats compact */}
      <section>
        <h2 className="font-serif text-2xl text-cocoa mb-4">Statistik</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="bg-white border border-sandBeige rounded-3xl p-6">
            <p className="wit-eyebrow text-warmGrey mb-2">Pipeline Startups</p>
            <p className="font-serif text-3xl text-cocoa mb-4">{stats.total_startups} gesamt</p>
            <div className="flex flex-col gap-2">
              {stats.pipeline_startups.length === 0 && <p className="text-sm text-warmGrey">Noch keine Daten.</p>}
              {stats.pipeline_startups.map((p) => (
                <div key={p.status} className="flex items-center gap-3">
                  <div className="w-32 shrink-0"><StatusBadge value={p.status} size="sm" /></div>
                  <div className="flex-1 bg-ivory rounded-full h-2 overflow-hidden">
                    <div
                      className="h-full bg-raspberry"
                      style={{ width: `${Math.min(100, (p.count / Math.max(1, stats.total_startups)) * 100)}%` }}
                    />
                  </div>
                  <span className="text-sm text-cocoa font-medium w-10 text-right">{p.count}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white border border-sandBeige rounded-3xl p-6">
            <p className="wit-eyebrow text-warmGrey mb-2">Pipeline Investor:innen</p>
            <p className="font-serif text-3xl text-cocoa mb-4">{stats.total_investors} gesamt</p>
            <div className="flex flex-col gap-2">
              {stats.pipeline_investors.length === 0 && <p className="text-sm text-warmGrey">Noch keine Daten.</p>}
              {stats.pipeline_investors.map((p) => (
                <div key={p.status} className="flex items-center gap-3">
                  <div className="w-32 shrink-0"><StatusBadge value={p.status} size="sm" /></div>
                  <div className="flex-1 bg-ivory rounded-full h-2 overflow-hidden">
                    <div
                      className="h-full bg-raspberry"
                      style={{ width: `${Math.min(100, (p.count / Math.max(1, stats.total_investors)) * 100)}%` }}
                    />
                  </div>
                  <span className="text-sm text-cocoa font-medium w-10 text-right">{p.count}</span>
                </div>
              ))}
            </div>
          </div>
          {stats.ai_mocked && (
            <div className="lg:col-span-2 bg-lavenderFog/40 border border-lavenderFog rounded-2xl px-5 py-3 text-sm text-cocoa flex items-center gap-3">
              <Sparkles size={14} className="text-raspberry" />
              <span>
                AI-Voranalyse läuft aktuell im <strong>Mock-Modus</strong>.
                Real-Mode aktivieren via <code className="text-xs">OPENAI_API_KEY</code> + <code className="text-xs">OPENAI_ANALYSIS_ENABLED=true</code>.
              </span>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
